"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();

import { useEffect, useMemo } from "react"
import { useQuery } from "@apollo/client/react"
import get from "lodash/get"
import useGetProvider from "./useGetProvider"
import useGetGroup from "./useGetGroup"
import useMyOrganizationList from "./useCurrentOrganization"
import useNavigationRequests from "./useNavigationRequests"
import { AUTH_ROLE_ZEST_PROVIDER } from "../lib/constants"
import { isOrgLeaderRole } from "../lib/create-profile/isOrgLeaderRole"
import { handleQueryError } from "../lib/logging"
import {
  computeOrganizationSetupState,
  countRemainingOrganizationSteps,
  getTotalOrganizationListingSteps,
  isOrganizationLegalComplianceComplete,
} from "../lib/provider/organizationSetupState"
import { GET_ORGANIZATION_SUBSCRIPTION } from "../graphql/queries/getOrganizationSubscription"
import LIST_ORGANIZATION_MEMBERS from "../graphql/queries/listOrganizationMembers"
import LIST_ORGANIZATION_INVITATIONS from "../graphql/queries/listInvitationsForOrganization"
import listOrganizationGroupsPage from "../graphql/queries/listOrganizationGroupsPage"

const TOTAL_PROVIDER_ONBOARDING_STEPS = 4

const ORG_INVITES_PAGE_SIZE = 100

function coerceLatestGroupIdToNumber(
  latestGroupId: string | number | boolean,
): number | null {
  if (typeof latestGroupId === "number" && Number.isFinite(latestGroupId)) {
    return latestGroupId
  }
  if (typeof latestGroupId === "string" && latestGroupId !== "") {
    const n = Number.parseInt(latestGroupId, 10)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/**
 * Matches leader dashboard: org listing uses 5-step `computeOrganizationSetupState`;
 * personal / no org uses provider `countRemainingOnboardingSteps` (4).
 */
export default function useMenuSetupStepsRemaining({
  userId,
  orgId,
}: {
  userId?: number | null
  orgId?: string | number | null
}) {
  const {
    providerId,
    enabled: navigationEnabled,
    authRole,
  } = useNavigationRequests()
  const enabled = navigationEnabled && authRole === AUTH_ROLE_ZEST_PROVIDER

  const [
    {
      provider,
      onboardingStatus,
      countRemainingOnboardingSteps: providerStepsRemaining,
      latestGroupId,
    },
  ] = useGetProvider({ enabled })

  const [{ group }] = useGetGroup({
    groupId: enabled ? coerceLatestGroupIdToNumber(latestGroupId) : null,
    organizationId: orgId != null ? String(orgId) : undefined,
  })

  const { currentOrganization, currentOrgIsPersonal, currentOrgRole } =
    useMyOrganizationList(
      userId != null ? String(userId) : undefined,
      orgId != null ? String(orgId) : undefined,
      { enabled },
    )

  const { data: membersData, error: membersQueryError } = useQuery(
    LIST_ORGANIZATION_MEMBERS,
    {
      variables: {
        iOrganizationId: orgId != null ? String(orgId) : "",
        first: 1,
      },
      skip: !enabled || !orgId,
    },
  )

  const { data: orgInvitesData, error: orgInvitesQueryError } = useQuery(
    LIST_ORGANIZATION_INVITATIONS,
    {
      variables: {
        organizationId: orgId != null ? String(orgId) : "",
        first: ORG_INVITES_PAGE_SIZE,
      },
      // Backend `list_invitations_for_organization` is restricted to org owners/admins.
      // Skip for non-leaders (facilitators) to avoid a guaranteed permission-denied error.
      skip: !enabled || !orgId || !isOrgLeaderRole(currentOrgRole),
    },
  )

  useEffect(() => {
    if (membersQueryError) {
      handleQueryError(membersQueryError)
    }
  }, [membersQueryError])

  useEffect(() => {
    if (orgInvitesQueryError) {
      handleQueryError(orgInvitesQueryError)
    }
  }, [orgInvitesQueryError])

  const organizationMembers = useMemo(
    () =>
      get(membersData, "listOrganizationMembers.edges", [])?.map(
        (edge: { node?: unknown } | null) => edge?.node,
      ) || [],
    [membersData],
  )

  const organizationMemberTotalCount =
    get(membersData, "listOrganizationMembers.totalCount") ?? null

  const organizationInvitations = useMemo(
    () =>
      get(orgInvitesData, "listInvitationsForOrganization.edges", [])
        ?.map((edge: { node?: unknown } | null) => edge?.node)
        ?.filter(Boolean) || [],
    [orgInvitesData],
  )

  const { data: subscriptionData, error: subscriptionQueryError } = useQuery(
    GET_ORGANIZATION_SUBSCRIPTION,
    {
      variables: {
        organizationId: orgId,
      },
      skip: !enabled || !orgId || !isOrgLeaderRole(currentOrgRole),
    },
  )

  useEffect(() => {
    if (subscriptionQueryError) {
      handleQueryError(subscriptionQueryError)
    }
  }, [subscriptionQueryError])

  const organizationSubscription =
    enabled && isOrgLeaderRole(currentOrgRole)
      ? get(subscriptionData, "organizationSubscription")
      : null

  const { data: orgGroupsData } = useQuery(listOrganizationGroupsPage, {
    variables: {
      pOrgId: orgId != null ? String(orgId) : "",
      first: 1,
      offset: 0,
    },
    skip: !enabled || !orgId || !currentOrganization || !!currentOrgIsPersonal,
  })
  const orgGroupTotalCount: number =
    get(orgGroupsData, "listOrganizationGroups.totalCount") ?? 0
  const orgHasPublishedGroups = orgGroupTotalCount > 0

  const surveyCompleted = !!providerId || !!provider

  const { setupStepsRemaining, setupTotalSteps } = useMemo(() => {
    const orgListingEligible =
      !currentOrgIsPersonal && currentOrganization && surveyCompleted

    if (orgListingEligible) {
      const organizationSetupState = computeOrganizationSetupState({
        organization: currentOrganization,
        onboardingStatus,
        group,
        provider,
        providerId,
        organizationMembers,
        organizationMemberTotalCount,
        organizationInvitations,
        organizationSubscription,
        roleInOrg: currentOrgRole,
        orgHasPublishedGroups,
        orgComplianceComplete:
          isOrganizationLegalComplianceComplete(currentOrganization),
      })
      return {
        setupStepsRemaining: countRemainingOrganizationSteps(organizationSetupState),
        setupTotalSteps: getTotalOrganizationListingSteps(
          currentOrgRole,
          orgHasPublishedGroups,
        ),
      }
    }

    return {
      setupStepsRemaining: providerStepsRemaining,
      setupTotalSteps: TOTAL_PROVIDER_ONBOARDING_STEPS,
    }
  }, [
    currentOrgIsPersonal,
    currentOrganization,
    surveyCompleted,
    onboardingStatus,
    group,
    provider,
    providerId,
    organizationMembers,
    organizationMemberTotalCount,
    organizationInvitations,
    organizationSubscription,
    providerStepsRemaining,
    currentOrgRole,
    orgHasPublishedGroups,
  ])

  return {
    setupStepsRemaining,
    setupTotalSteps,
    organizationSubscription,
  }
}
